import { Flex, Text, Tooltip, Badge } from '@chakra-ui/react';
import {
  faHourglassHalf,
  faInfoCircle
} from '@fortawesome/pro-regular-svg-icons';
import { Accordion, ButtonIcon } from '@morressier/alembic';
import {
  ICoReview,
  IInvitedReviewer,
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment,
  IReviewer,
  IReviewerFeedback,
  IReviewTimeline,
  getRevisionSeverityText
} from '@publishing-engine/domain/models';
import {
  DocumentSchemaResponse,
  IGetRevisionHistory,
  IGetRevisionHistoryResponse
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import dateFormat from 'dateformat';
import { Fragment } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { formatDate } from 'src/utils/date';
import { IconBadge } from 'src/components/IconBadge';
import ReviewerRevisionOverviewContent from './ReviewerRevisionOverviewContent';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Props {
  onReinviteReviewer?: (reviewer: IInvitedReviewer) => void;
  onReassignBoardMember?: (reviewer: IInvitedReviewer) => void;
  onRateReview?: (reviewer: IInvitedReviewer, receiveFeedback?: string) => void;
}

export type ReviewerFeedbackType = IReviewerFeedback & {
  reviewAssessment?:
    | IPeerReviewFirstAssessment
    | IPeerReviewRevisionAssessment
    | null;
  reviewer?: IReviewer | null;
  coReview?: ICoReview | null;
  timeline?: IReviewTimeline | null;
};

export const getReviewHistoryAccordionTitle = (
  revision: IGetRevisionHistory,
  round: number,
  transferredManuscriptId?: string
) => {
  const date = `${dateFormat(
    revision?.dateEntered!,
    'mmmm d, yyyy'
  )} to ${dateFormat(revision?.dateCompleted!, 'mmmm d, yyyy')}`;

  const idPrefix = transferredManuscriptId
    ? `${transferredManuscriptId} | `
    : '';

  return round === 1
    ? `${idPrefix} Original Submission (${date})`
    : `${idPrefix} Revision - ${round - 1} (${date})`;
};

export const getReviewHistoryAccordionSubTitle = (
  revisionHistory: IGetRevisionHistory[],
  revision: IGetRevisionHistory,
  index: number
) => {
  if (revision.type === 'immediate') return 'Immediate Revision';

  return index === (revisionHistory?.length ?? 0) - 1
    ? `Peer Review -> ${
        getRevisionSeverityText(revision.severity) ||
        (revision.type === 'transfer' ? 'Transfer Offered' : 'Peer Review')
      }`
    : `${
        getRevisionSeverityText(revisionHistory?.[index + 1]?.severity) ||
        (revision.type === 'review' ? 'Peer Review' : 'Immediate Revision')
      } -> ${
        getRevisionSeverityText(revisionHistory?.[index]?.severity) ||
        (revision.type === 'transfer' ? 'Transfer Offered' : 'Peer Review')
      }`;
};

const getReviewFormField = (documentSchema?: DocumentSchemaResponse) => {
  if (!documentSchema) {
    return null;
  }

  const reviewFormFields = new ReviewFormFields(documentSchema);

  const config = {
    reviewScore: reviewFormFields.getFieldConfig('ReviewScore'),
    //#region  submission score fields
    originality: reviewFormFields.getFieldConfig('Originality'),
    significance: reviewFormFields.getFieldConfig('Significance'),
    rigour: reviewFormFields.getFieldConfig('Rigour'),
    clarity: reviewFormFields.getFieldConfig('Clarity'),
    impact: reviewFormFields.getFieldConfig('Impact'),
    //#endregion
    missingReference: reviewFormFields.getFieldConfig('MissingReference'),
    articleWellWritten: reviewFormFields.getFieldConfig('ArticleWellWritten'),
    areaOfExpertise: reviewFormFields.getFieldConfig('AreaOfExpertise'),
    conflictOfInterest: reviewFormFields.getFieldConfig('ConflictOfInterest'),
    canArticleBeShortened: reviewFormFields.getFieldConfig(
      'CanArticleBeShortened'
    ),
    meritFurtherCoverageUponPublication: reviewFormFields.getFieldConfig(
      'MeritFurtherCoverageUponPublication'
    ),
    betterSuitedForAnotherJournal: reviewFormFields.getFieldConfig(
      'BetterSuitedForAnotherJournal'
    ),
    webOfScienceOptIn: reviewFormFields.getFieldConfig('WebOfScienceOptIn'),
    peerReviewTransparency: reviewFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    reviewerAnonymity: reviewFormFields.getFieldConfig('ReviewerAnonymity'),
    consentToReInvite: reviewFormFields.getFieldConfig('ConsentToReInvite'),
    receiveFeedback: reviewFormFields.getFieldConfig('ReceiveFeedback'),
    unaddressedReviewComments: reviewFormFields.getFieldConfig(
      'UnaddressedReviewComments'
    )
  };

  return config;
};

const renderRevisionRoundGroup = (
  revisionHistory: IGetRevisionHistoryResponse,
  revisionGroup: {
    journalName: string;
    rounds: IGetRevisionHistory[];
    dateEntered?: string;
  },
  isCurrent: boolean,
  index: number = 0,
  actions?: {
    onReinviteReviewer?: (reviewer: IInvitedReviewer) => void;
    onReassignBoardMember?: (reviewer: IInvitedReviewer) => void;
    onRateReview?: (
      reviewer: IInvitedReviewer,
      receiveFeedback?: string
    ) => void;
  }
) => {
  const { journalName, rounds, dateEntered } = revisionGroup;

  const transferredJournalName =
    index === 0
      ? revisionHistory?.current.journalName
      : revisionHistory?.previous[index - 1]?.journalName;

  const transferredManuscriptId = isCurrent
    ? revisionHistory?.current.manuscriptId
    : revisionHistory?.previous[index]?.manuscriptId;
  return (
    <Flex paddingRight={5} flexDirection="column" gap={4}>
      <Flex flexDirection="column" gap={2}>
        <Flex justifyContent="space-between">
          <Text textStyle="textBody" fontWeight="semibold">
            {journalName} Feedback
          </Text>
          <Badge variant={isCurrent ? 'attention' : 'neutral'} size="context">
            {isCurrent ? 'Current Journal' : 'Transferred'}
          </Badge>
        </Flex>
        {!isCurrent && (
          <Text textStyle="textBody">
            Transferred to {transferredJournalName} on{' '}
            {formatDate(new Date(dateEntered ?? ''))}
          </Text>
        )}
      </Flex>
      {rounds.length > 0 ? (
        <Accordion
          data-testId="revisions-history-overview"
          chakraAccordionProps={{
            allowMultiple: true,
            defaultIndex: [],
            style: {
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          accordionItemProps={{
            background: 'neutralPrimaryContentInverse',
            marginBottom: 0
          }}
          items={rounds?.map((round, index) => {
            const roundNumber = rounds.length - index;
            // @NOTE: in every revision round, for each feedback has same reviewAssessmentSchemaId. Here we will find() first one which has it.
            // yes it is possible that some feedback contains just empty values and reviewId, that could be because that reviewer has not submitted review.
            // we are returning reviewId for such cases, so we can show coReviewer info even if reviewAssessment (feedback) is not present.
            const reviewAssessmentSchemaId =
              round.reviewerRevisionFeedback?.find(
                feedback => !!feedback.reviewAssessmentSchemaId
              )?.reviewAssessmentSchemaId || ''; // find first non-empty reviewAssessmentSchemaId

            const fieldConfig = getReviewFormField(
              revisionHistory?.documentSchemas[reviewAssessmentSchemaId]
            );

            return {
              title: getReviewHistoryAccordionTitle(
                round,
                roundNumber,
                transferredManuscriptId
              ),
              subtitle: getReviewHistoryAccordionSubTitle(rounds, round, index),
              content: (
                <ReviewerRevisionOverviewContent
                  revision={round}
                  revisionRound={roundNumber}
                  reviewFieldConfig={fieldConfig}
                  onReinviteReviewer={actions?.onReinviteReviewer}
                  onReassignBoardMember={actions?.onReassignBoardMember}
                  onRateReview={actions?.onRateReview}
                />
              )
            };
          })}
        />
      ) : (
        <Flex flexDirection="column" gap={2}>
          <Flex gap={2}>
            <IconBadge icon={faHourglassHalf} variant="attention" />
            <Text textStyle="headingLine">No Review History Yet</Text>
          </Flex>
          <Text textStyle="textBody">
            Review history will be recorded once a starting peer review round is
            closed.
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export const ReviewsHistory: React.FC<Props> = ({
  onReinviteReviewer,
  onReassignBoardMember,
  onRateReview
}) => {
  const { revisionHistory } = useSubmissionData();

  const hasRevisionHistory = !!(
    revisionHistory?.current.rounds.length || revisionHistory?.previous.length
  );

  return (
    <>
      {hasRevisionHistory && (
        <Flex flexDirection="column" gap={6}>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingSection" id="review-history">
              Review Rounds
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label="View details of all peer review rounds, including reviewer
              feedback and manuscript versions evaluated during each stage"
                borderRadius={4}
              >
                <ButtonIcon
                  size="small"
                  ml={1}
                  ariaLabel="Help Icon"
                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                />
              </Tooltip>
            </Text>
          </Flex>
          {revisionHistory.current.rounds.length > 0 &&
            renderRevisionRoundGroup(
              revisionHistory,
              revisionHistory.current,
              true,
              0,
              { onReinviteReviewer, onReassignBoardMember, onRateReview }
            )}

          {revisionHistory.previous.length > 0 &&
            revisionHistory.previous.map(
              (revisionGroup, index) =>
                revisionGroup.rounds.length > 0 && (
                  <Fragment key={revisionGroup.id}>
                    {renderRevisionRoundGroup(
                      revisionHistory,
                      revisionGroup,
                      false,
                      index,
                      {
                        onReinviteReviewer,
                        onReassignBoardMember,
                        onRateReview
                      }
                    )}
                  </Fragment>
                )
            )}
        </Flex>
      )}
    </>
  );
};
